import React, { useCallback, useEffect, useState } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	makeStyles,
	MenuItem,
	TextField,
	Typography,
} from "@material-ui/core";
import { ArrowDownward, ArrowUpward, DeleteOutline } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	row: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	name: { flex: "2 1 150px" },
	kind: { flex: "1 1 120px" },
	stale: { flex: "1 1 110px" },
	color: { width: 40, height: 32, border: 0, padding: 0, background: "none", cursor: "pointer" },
	hint: { marginBottom: theme.spacing(1), color: theme.palette.text.secondary },
}));

/** Admin-only: rename, recolor, reorder, add and remove funnel stages. */
const StagesDialog = ({ open, onClose, onChanged }) => {
	const classes = useStyles();
	const [stages, setStages] = useState([]);
	const [draft, setDraft] = useState({});

	const load = useCallback(async () => {
		try {
			const { data } = await api.get("/funnel/stages");
			setStages(data.stages || []);
			setDraft({});
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		if (open) void load();
	}, [open, load]);

	const field = (stage, key) => (draft[stage.id] && draft[stage.id][key] !== undefined ? draft[stage.id][key] : stage[key] ?? "");
	const edit = (stage, key, value) =>
		setDraft(prev => ({ ...prev, [stage.id]: { ...(prev[stage.id] || {}), [key]: value } }));

	const run = async fn => {
		try {
			await fn();
			await load();
			onChanged();
		} catch (err) {
			toastError(err);
		}
	};

	const saveStage = stage =>
		run(async () => {
			await api.put(`/funnel/stages/${stage.id}`, draft[stage.id]);
			toast.success(i18n.t("funnel.stages.saved"));
		});

	const move = (index, delta) => {
		const ids = stages.map(s => s.id);
		const target = index + delta;
		if (target < 0 || target >= ids.length) return;
		[ids[index], ids[target]] = [ids[target], ids[index]];
		void run(() => api.post("/funnel/stages/reorder", { ids }));
	};

	const remove = stage => run(() => api.delete(`/funnel/stages/${stage.id}`));

	const add = () =>
		run(() => api.post("/funnel/stages", { name: i18n.t("funnel.stages.newName"), kind: "open" }));

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{i18n.t("funnel.stages.title")}</DialogTitle>
			<DialogContent dividers>
				<Typography variant="body2" className={classes.hint}>
					{i18n.t("funnel.stages.hint")}
				</Typography>
				{stages.map((stage, index) => (
					<div key={stage.id} className={classes.row}>
						<input
							type="color"
							className={classes.color}
							aria-label={i18n.t("funnel.stages.color")}
							value={field(stage, "color")}
							onChange={e => edit(stage, "color", e.target.value)}
						/>
						<TextField
							className={classes.name}
							id={`stage-name-${stage.id}`}
							label={i18n.t("funnel.stages.name")}
							value={field(stage, "name")}
							onChange={e => edit(stage, "name", e.target.value)}
							inputProps={{ maxLength: 60 }}
							variant="outlined"
							size="small"
						/>
						<TextField
							select
							className={classes.kind}
							id={`stage-kind-${stage.id}`}
							label={i18n.t("funnel.stages.kind")}
							value={field(stage, "kind")}
							onChange={e => edit(stage, "kind", e.target.value)}
							variant="outlined"
							size="small"
						>
							{["open", "won", "lost"].map(k => (
								<MenuItem key={k} value={k}>
									{i18n.t(`funnel.kind.${k}`)}
								</MenuItem>
							))}
						</TextField>
						<TextField
							className={classes.stale}
							type="number"
							id={`stage-stale-${stage.id}`}
							label={i18n.t("funnel.stages.staleDays")}
							value={field(stage, "staleDays")}
							onChange={e => edit(stage, "staleDays", e.target.value)}
							disabled={field(stage, "kind") !== "open"}
							inputProps={{ min: 1, max: 365 }}
							variant="outlined"
							size="small"
						/>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							disabled={!draft[stage.id]}
							onClick={() => saveStage(stage)}
						>
							{i18n.t("funnel.stages.save")}
						</Button>
						<IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)} aria-label={i18n.t("funnel.stages.up")}>
							<ArrowUpward fontSize="small" />
						</IconButton>
						<IconButton size="small" disabled={index === stages.length - 1} onClick={() => move(index, 1)} aria-label={i18n.t("funnel.stages.down")}>
							<ArrowDownward fontSize="small" />
						</IconButton>
						<IconButton size="small" onClick={() => remove(stage)} aria-label={i18n.t("funnel.stages.remove")}>
							<DeleteOutline fontSize="small" />
						</IconButton>
					</div>
				))}
			</DialogContent>
			<DialogActions>
				<Button onClick={add}>{i18n.t("funnel.stages.add")}</Button>
				<Button onClick={onClose} color="primary" variant="contained">
					{i18n.t("funnel.stages.close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default StagesDialog;
